/**
 * sanitizeAgreementHtml.ts — the second line under the agreement reader.
 *
 * `agreement-markdown.ts` already runs markdown-it with `html: false`, so a raw tag in a
 * template or in a merged value is escaped into text rather than parsed. This module is
 * the belt to that pair of braces: the markdown a driver reads carries merge values that
 * came out of free-text database columns, and the reader mounts the result with
 * `dangerouslySetInnerHTML`, which is exactly the pairing that deserves a second opinion.
 *
 * The allowlist is the tag set the PDF renderer can actually draw — headings, paragraphs,
 * lists, GFM tables, `<strong>`, rules — plus the container wrappers this app adds.
 * No links (a signed instrument cannot carry a live target honestly), no images, no
 * styles, no ids, no event handlers.
 *
 * ── THE CONTAINER WRAPPERS COST THE ALLOWLIST NOTHING ─────────────────────────
 * `:::keeptogether:::`, `:::signature:::`, `:::particulars:::`, `:::box:::`,
 * `:::included:::` and `:::excluded:::` all fold to a `<div>` carrying a `class`, and the
 * differences between them — a bordered panel, a ruled particulars grid, ticks instead of
 * bullets — are drawn by the reader's stylesheet, never by markup. So the list below is the
 * same list six containers later: no `<svg>`, no `<img>`, no inline `style` of our own. A
 * new container is a new class, and a class cannot widen what a driver's browser will mount.
 *
 * DOMPurify needs a window, which is why this lives apart from the pure parser.
 */

import DOMPurify from 'dompurify';

/**
 * Exactly what `agreementMarkdownToPages` can produce, and nothing else.
 *
 * Exported so the parser's own test — which runs in the node environment DOMPurify cannot
 * live in — can assert that every tag the emitter writes is a tag this list admits. That
 * assertion is the seam where the two files would otherwise drift apart silently: HTML the
 * sanitiser strips is content the driver never reads, and nothing else would notice.
 */
export const ALLOWED_TAGS = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'strong', 'em', 'code',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'pre',
    'div', 'span',
];

/**
 * `class` carries our container marks and `data-role` names the signature party. `style`
 * is here for one reason only: markdown-it writes GFM column alignment as
 * `style="text-align:right"` on a `th`/`td`, and a money column that loses its alignment
 * reads as a different table. DOMPurify runs its own CSS parser over the value.
 *
 * Exported for the same reason as the tag list: the parser's test checks its own output
 * against it.
 */
export const ALLOWED_ATTR = ['class', 'data-role', 'style'];

export function sanitizeAgreementHtml(html: string): string {
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input', 'a', 'img'],
        FORBID_ATTR: ['id', 'onerror', 'onload', 'srcset', 'href', 'src'],
    });
}
